import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/notifications';
import type { NotificationSettingInput, PaginationParams } from '@/types';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: object = {}) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  settings: ['notifications', 'settings'] as const,
};

export function useNotifications(params: PaginationParams & { unreadOnly?: boolean } = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 60_000,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useNotificationSettings(params: PaginationParams = {}) {
  return useQuery({
    queryKey: [...notificationKeys.settings, params],
    queryFn: () => notificationsApi.listSettings(params),
  });
}

export function useCreateNotificationSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationSettingInput) => notificationsApi.createSettings(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.settings }),
  });
}

export function useUpdateNotificationSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: NotificationSettingInput }) =>
      notificationsApi.updateSettings(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.settings }),
  });
}

export function useDeleteNotificationSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.deleteSettings(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.settings }),
  });
}
